import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Button,
} from "@mui/material";
import {
  TabletMacOutlined,
  ReceiptLongOutlined,
  SettingsEthernetOutlined,
} from "@mui/icons-material";

const ProductCard = ({ icon, title, subtitle, bullets, chip }) => (
  <Card
    variant="outlined"
    sx={{
      borderRadius: 4,
      bgcolor: "rgba(15,23,42,0.85)",
      borderColor: "rgba(148,163,184,0.25)",
      height: "100%",
    }}
  >
    <CardContent>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(37,99,235,0.18)",
              color: "#fff",
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: "#fff", fontWeight: 800 }}>
              {title}
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(226,232,240,0.7)" }}>
              {subtitle}
            </Typography>
          </Box>
        </Stack>

        {chip && (
          <Chip
            label={chip}
            color="primary"
            sx={{
              width: "fit-content",
              fontWeight: 800,
              bgcolor: "primary.main",
              color: "primary.contrastText",
            }}
          />
        )}

        <Stack spacing={0.75} sx={{ color: "rgba(226,232,240,0.85)" }}>
          {bullets.map((b, i) => (
            <Typography key={i} variant="body2">
              • {b}
            </Typography>
          ))}
        </Stack>
      </Stack>
    </CardContent>
  </Card>
);

const ProductsOverview = () => {
  return (
    <Box component="section" sx={{ py: 8 }}>
      <Stack spacing={2} sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{ color: "#fff", fontWeight: 900, fontFamily: "Poppins, sans-serif" }}
        >
          US Fuel Products
        </Typography>
        <Typography variant="h4" sx={{ color: "rgba(226,232,240,0.8)", maxWidth: 900 }}>
          Three products that work together to modernize legacy ATGs and eliminate manual compliance work.
          Pair <b>ATG Pro</b> with <b>BackOffice</b> to make reporting fully automated.
        </Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip
            label="Fully Automated SIR Reporting"
            sx={{ bgcolor: "rgba(34,197,94,0.18)", color: "#fff", fontWeight: 800 }}
          />
          <Chip
            label="Sales + Delivery Reconciliation"
            sx={{ bgcolor: "rgba(59,130,246,0.18)", color: "#fff", fontWeight: 800 }}
          />
          <Chip
            label="Retrofit: no ATG replacement"
            sx={{ bgcolor: "rgba(148,163,184,0.18)", color: "#fff", fontWeight: 800 }}
          />
        </Stack>
      </Stack>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <ProductCard
            icon={<TabletMacOutlined />}
            title="ATG Pro"
            subtitle="7” touchscreen + CPU/networking retrofit for legacy ATGs"
            chip='Hardware + Console Experience'
            bullets={[
              "Live tank dashboard (volume, ullage, water, temp)",
              "Remote monitoring + alarms",
              "Cloud storage + compliance exports",
            ]}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <ProductCard
            icon={<ReceiptLongOutlined />}
            title="BackOffice"
            subtitle="Connects to your site backoffice PC to read fuel sales data"
            chip="Fully Automated Compliance"
            bullets={[
              "Reads fuel sales data from the backoffice",
              "Auto-reconciliation vs ATG inventory + deliveries",
              "Automated SIR reports (no spreadsheets)",
            ]}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <ProductCard
            icon={<SettingsEthernetOutlined />}
            title="ATG Commander"
            subtitle="Desktop software for technicians (USB) to backup/restore ATG configs"
            chip="Technician Tooling"
            bullets={[
              "Save full ATG config to a file",
              "Reflash the exact config back to an ATG",
              "Standardize settings across multiple sites",
            ]}
          />
        </Grid>
      </Grid>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 4 }}>
        <Button variant="contained" size="large" color="primary" href="#contact" sx={{ fontWeight: 800 }}>
          Talk to Sales
        </Button>
        <Button variant="outlined" size="large" color="inherit" href="#atg-pro" sx={{ borderColor: "rgba(255,255,255,0.35)" }}>
          Explore ATG Pro
        </Button>
      </Stack>
    </Box>
  );
};

export default ProductsOverview;
